import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, Plus, Trash2, CheckCircle2, ShieldCheck } from "lucide-react";

const brandColor = { Visa: "from-blue-600 to-blue-400", Mastercard: "from-orange-600 to-red-500", Amex: "from-teal-600 to-cyan-500", Card: "from-slate-600 to-slate-400" };

export const BuyerPayments = () => {
  const [cards, setCards] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ holder: "", number: "", exp_month: "", exp_year: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/buyer/payment-methods").then((r) => setCards(r.data)).catch(() => setCards([])); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.holder.trim() || !form.number.trim() || !form.exp_month || !form.exp_year) return toast.error("Fill in all fields");
    setSaving(true);
    try {
      await api.post("/buyer/payment-methods", { holder: form.holder, number: form.number, exp_month: parseInt(form.exp_month, 10), exp_year: parseInt(form.exp_year, 10) });
      toast.success("Card saved"); setOpen(false); setForm({ holder: "", number: "", exp_month: "", exp_year: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const setDefault = async (id) => { await api.post(`/buyer/payment-methods/${id}/default`); load(); };
  const del = async (id) => { if (!window.confirm("Remove this card?")) return; await api.delete(`/buyer/payment-methods/${id}`); toast.success("Card removed"); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" />Cards are saved for convenience. Payments are securely processed by Stripe at checkout.</p>
        <Button className="rounded-full shrink-0" data-testid="add-card-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" />Add card</Button>
      </div>

      {cards === null ? (
        <div className="grid sm:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="h-44 rounded-2xl shimmer" />)}</div>
      ) : cards.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground rounded-2xl"><CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />No saved cards yet.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {cards.map((c) => (
            <div key={c.method_id} data-testid={`card-${c.method_id}`} className={`rounded-2xl p-5 text-white bg-gradient-to-br ${brandColor[c.brand] || brandColor.Card} shadow-lg relative`}>
              <div className="flex justify-between items-start">
                <CreditCard className="w-8 h-8 opacity-90" />
                {c.is_default && <Badge className="bg-white/25 text-white">Default</Badge>}
              </div>
              <p className="text-xl font-mono tracking-widest mt-6">•••• •••• •••• {c.last4}</p>
              <div className="flex justify-between items-end mt-4 text-sm">
                <div><p className="text-[10px] opacity-70 uppercase">Card holder</p><p className="font-medium">{c.holder}</p></div>
                <div className="text-right"><p className="text-[10px] opacity-70 uppercase">Expires</p><p className="font-medium">{String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</p></div>
              </div>
              <div className="flex gap-2 mt-4">
                {!c.is_default && <Button size="sm" variant="secondary" className="h-7 text-xs" data-testid={`card-default-${c.method_id}`} onClick={() => setDefault(c.method_id)}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Set default</Button>}
                <Button size="sm" variant="secondary" className="h-7 text-xs" data-testid={`card-del-${c.method_id}`} onClick={() => del(c.method_id)}><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-card-dialog">
          <DialogHeader><DialogTitle>Add a card</DialogTitle><DialogDescription>We only store the last 4 digits — never the full number or CVV.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Card holder</Label><Input data-testid="card-holder" value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Card number</Label><Input data-testid="card-number" inputMode="numeric" placeholder="4242 4242 4242 4242" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Exp. month</Label><Input data-testid="card-exp-month" inputMode="numeric" placeholder="12" value={form.exp_month} onChange={(e) => setForm({ ...form, exp_month: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Exp. year</Label><Input data-testid="card-exp-year" inputMode="numeric" placeholder="2028" value={form.exp_year} onChange={(e) => setForm({ ...form, exp_year: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-full w-full" data-testid="save-card-btn" onClick={add} disabled={saving}>{saving ? "Saving…" : "Save card"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
